/* L'amministrazione delle utenze.
 *
 * **La parola d'ordine si mostra una volta.** Compare in un riquadro dedicato,
 * e da quel riquadro si esce con un gesto esplicito: non si perde per un
 * rinfresco della pagina, e non torna piu'. Non e' un vezzo di sicurezza — non
 * e' rileggibile nemmeno dal servizio, in archivio ne resta l'impronta — ed e'
 * per questo che la schermata deve dire chiaramente che quella e' l'unica
 * occasione. Chi la chiude prima di averla trascritta deve generarne un'altra.
 *
 * **L'ultimo manager lo protegge il servizio.** Qui non si conta niente: si
 * chiede, e si legge il rifiuto `last_manager` per esteso. Contarlo nella
 * pagina darebbe una risposta vecchia di quanto e' vecchio l'elenco, e due
 * schede aperte insieme si direbbero l'una all'altra che l'altro c'e' ancora.
 */
import { useEffect, useState } from 'react'

import { ServiceError } from '../../api'
import {
  changeAccount,
  describeRole,
  namedRole,
  openAccount,
  readAccounts,
  readRoles,
  resetPassword,
  ROLES,
  type Role,
  type Account,
  type Opened,
} from '../../admin/users'
import { useLocale, useT } from '../../i18n/LocaleProvider'
import { t as translate } from '../../i18n/t'

export function UserAdmin(): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const [accounts, setAccounts] = useState<Account[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [handed, setHanded] = useState<Opened | null>(null)
  const [login, setLogin] = useState('')
  const [displayName, setDisplayName] = useState('')
  // Nessun ruolo preimpostato: un valore predefinito e' il modo di aprire un
  // supervisore senza averci pensato.
  const [role, setRole] = useState('')
  const [roles, setRoles] = useState<Role[] | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void readRoles()
      .then(setRoles)
      .catch(() => {
        // Senza matrice la scelta resta possibile, solo non spiegata.
      })
    let cancelled = false
    void readAccounts()
      .then((found) => {
        if (!cancelled) setAccounts(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'users.readFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  async function refresh(): Promise<void> {
    setAccounts(await readAccounts())
  }

  async function act(what: () => Promise<void>): Promise<void> {
    setBusy(true)
    setFailure(null)
    try {
      await what()
    } catch (error: unknown) {
      setFailure(read(error, t('users.requestFailed')))
    } finally {
      setBusy(false)
    }
  }

  function onOpen(event: React.FormEvent): void {
    event.preventDefault()
    void act(async () => {
      const opened = await openAccount({ login, display_name: displayName, role })
      setHanded(opened)
      setLogin('')
      setDisplayName('')
      await refresh()
    })
  }

  function onReset(account: Account): void {
    void act(async () => {
      setHanded(await resetPassword(account.id))
      await refresh()
    })
  }

  function onRole(account: Account, next: string): void {
    if (next === account.role) return
    void act(async () => {
      await changeAccount(account.id, { role: next })
      await refresh()
    })
  }

  function onActive(account: Account, next: boolean): void {
    void act(async () => {
      await changeAccount(account.id, { active: next })
      await refresh()
    })
  }

  return (
    <main className="amministrazione">
      <h2>{t('users.heading')}</h2>

      {failure !== null && <p role="alert">{failure}</p>}

      {handed !== null && (
        <section className="consegna-parola" aria-labelledby="parola-titolo">
          <h3 id="parola-titolo">{t('users.passwordTitle')}</h3>
          <p>{t('users.passwordAccount', { login: handed.account.login, name: handed.account.display_name })}</p>
          <p>
            <code>{handed.password}</code>
          </p>
          <p role="note">{t('users.passwordNote')}</p>
          <button type="button" onClick={() => setHanded(null)}>
            {t('users.passwordClose')}
          </button>
        </section>
      )}

      <section aria-labelledby="apri-titolo">
        <h3 id="apri-titolo">{t('users.openTitle')}</h3>
        <form onSubmit={onOpen}>
          <label>
            {t('login.username')}
            <input
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              maxLength={64}
              required
            />
          </label>
          <label>
            {t('users.displayName')}
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={128}
              required
            />
          </label>
          <label>
            {t('users.role')}
            <select value={role} onChange={(event) => setRole(event.target.value)} required>
              <option value="" disabled>
                {t('users.chooseRole')}
              </option>
              {ROLES.map((one) => (
                <option key={one.value} value={one.value}>
                  {namedRole(t, one.value)}
                </option>
              ))}
            </select>
          </label>
          <RoleSays role={role} roles={roles} />
          <button className="primario" type="submit" disabled={busy}>
            {t('users.openSubmit')}
          </button>
        </form>
        <p className="tenue">{t('users.openHint')}</p>
        <p className="tenue">{t('users.roleFirst')}</p>
      </section>

      <section aria-labelledby="elenco-titolo">
        <h3 id="elenco-titolo">{t('users.listTitle')}</h3>
        {accounts === null ? (
          <p role="status">{t('users.loading')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th scope="col">{t('users.colLogin')}</th>
                <th scope="col">{t('users.colName')}</th>
                <th scope="col">{t('users.colRole')}</th>
                <th scope="col">{t('users.colStatus')}</th>
                <th scope="col">{t('users.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    <code>{account.login}</code>
                  </td>
                  <td>{account.display_name}</td>
                  <td>
                    <label className="solo-lettori" htmlFor={`ruolo-${account.id}`}>
                      {t('users.roleOf', { login: account.login })}
                    </label>
                    <select
                      id={`ruolo-${account.id}`}
                      value={account.role}
                      disabled={busy}
                      onChange={(event) => onRole(account, event.target.value)}
                    >
                      {ROLES.map((one) => (
                        <option key={one.value} value={one.value}>
                          {namedRole(t, one.value)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {account.active ? t('users.active') : t('users.inactive')}
                    {account.must_change_password && (
                      <span className="tenue"> · {t('users.mustChange')}</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onActive(account, !account.active)}
                    >
                      {account.active ? t('users.deactivate') : t('users.reactivate')}
                    </button>
                    <button type="button" disabled={busy} onClick={() => onReset(account)}>
                      {t('users.resetPassword')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="tenue">{t('users.listHint')}</p>
      </section>
    </main>
  )
}

function read(error: unknown, fallback: string): string {
  return error instanceof ServiceError ? error.message : fallback
}


/**
 * Che cosa fa il ruolo scelto, e che cosa non fa.
 *
 * Nel momento della scelta e non dopo: e' l'unico istante in cui la frase
 * serve. Il testo viene dalla matrice del servizio, che e' quella che rifiuta
 * davvero — se qui c'e' scritto «non consegna», e' perche' la consegna gli
 * verrebbe negata.
 */
function RoleSays({ role, roles }: { role: string; roles: Role[] | null }): JSX.Element | null {
  const t = useT()
  const found = roles?.find((one) => one.role === role)
  if (role === '' || found === undefined) return null
  const said = describeRole(found.may)
  return (
    <p className="ruolo-dice" role="note">
      {t('role.doesIntro')} {said.does.map((key) => t(key)).join('; ')}
      {said.not.length > 0 && ` — ${t('role.notIntro')} ${said.not.map((key) => t(key)).join(', ')}`}.
    </p>
  )
}
